import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  query,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { firestore } from '../../../../services/firebase';
import { CATALOG_FILTERS_SUBCOLLECTION } from '../../../../constants/productCatalogFilters';
import { FILTER_NAME_REGEX, nameToFilterSlug } from '../../../../utils/filterSlug';
import { toViewableImageUrl } from '../../../../utils/catalogProductImageStorage';

function getCardCoverUrlFromData(data) {
  const rawUrl = data?.backgroundImage?.url || data?.img || data?.image || null;
  return rawUrl ? toViewableImageUrl(rawUrl) : null;
}

function filtersCollectionRef(collectionName, docId) {
  return collection(firestore, collectionName, docId, CATALOG_FILTERS_SUBCOLLECTION);
}

function normalizeProductAccessIds(accessIds = []) {
  return accessIds.flatMap((entry) => {
    if (entry == null) return [];
    if (typeof entry === 'string' || typeof entry === 'number') return [String(entry)];
    if (typeof entry === 'object' && entry.id != null) return [String(entry.id)];
    return [];
  });
}

export { normalizeProductAccessIds };

export function cardBelongsToProductAccess(card, accessIds = []) {
  if (!accessIds.length) return false;

  const ids = new Set(normalizeProductAccessIds(accessIds));
  if ((card.productIds || []).some((id) => ids.has(String(id)))) return true;

  if ((card.plans || []).some((plan) => ids.has(String(plan.productId)))) return true;

  if ((card.coursesCard || []).some((course) => ids.has(String(course.productId)))) {
    return true;
  }

  return false;
}

export async function loadProductCatalogFilters(collectionName, docId) {
  if (!collectionName || !docId) return [];

  const snap = await getDocs(
    query(filtersCollectionRef(collectionName, docId), orderBy('order', 'asc'))
  );

  return snap.docs.map((docSnap) => {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      name: data.name ?? '',
      slug: data.slug ?? '',
      order: data.order ?? 0,
      archived: !!data.archived,
      categoryIds: Array.isArray(data.categoryIds) ? data.categoryIds : [],
      cardEntries: Array.isArray(data.cardEntries) ? data.cardEntries : [],
    };
  });
}

export async function loadProductCatalogFilterTree(accessIds) {
  const catsSnap = await getDocs(
    query(collection(firestore, 'categories'), orderBy('order', 'asc'))
  );

  const tree = [];

  for (const catDoc of catsSnap.docs) {
    const cardsSnap = await getDocs(
      query(collection(firestore, 'categories', catDoc.id, 'cards'), orderBy('order', 'asc'))
    );

    const cards = cardsSnap.docs
      .map((cardDoc) => ({
        id: cardDoc.id,
        title: cardDoc.data().title ?? '',
        productIds: cardDoc.data().productIds || [],
        plans: cardDoc.data().plans || [],
        coursesCard: cardDoc.data().coursesCard || [],
        selected: false,
      }))
      .filter((card) => cardBelongsToProductAccess(card, accessIds));

    if (cards.length === 0) continue;

    tree.push({
      id: catDoc.id,
      title: catDoc.data().title ?? '',
      selected: false,
      cards,
    });
  }

  return tree;
}

const MAX_MODULE_COVER_REFERENCES = 20;

/**
 * Capas horizontais de módulos vinculados ao produto (catálogo interno).
 */
export async function loadProductCatalogModuleCoverReferenceOptions(accessIds = []) {
  if (!accessIds.length) return [];

  const catsSnap = await getDocs(
    query(collection(firestore, 'categories'), orderBy('order', 'asc'))
  );

  const options = [];
  const seenUrls = new Set();

  for (const catDoc of catsSnap.docs) {
    const categoryTitle = catDoc.data().title ?? '(sem título)';
    const cardsSnap = await getDocs(
      query(collection(firestore, 'categories', catDoc.id, 'cards'), orderBy('order', 'asc'))
    );

    for (const cardDoc of cardsSnap.docs) {
      const data = cardDoc.data();
      const card = {
        productIds: data.productIds || [],
        plans: data.plans || [],
        coursesCard: data.coursesCard || [],
      };

      if (!cardBelongsToProductAccess(card, accessIds)) continue;

      const url = getCardCoverUrlFromData(data);
      if (!url || seenUrls.has(url)) continue;
      seenUrls.add(url);

      options.push({
        id: url,
        label: data.title ?? '(sem título)',
        subtitle: `Módulo do catálogo — ${categoryTitle}`,
        url,
        kind: 'catalog-module',
        categoryTitle,
      });
    }
  }

  return options
    .sort((a, b) => {
      const categoryCompare = (a.categoryTitle || '').localeCompare(b.categoryTitle || '', 'pt-BR');
      if (categoryCompare !== 0) return categoryCompare;
      return a.label.localeCompare(b.label, 'pt-BR');
    })
    .slice(0, MAX_MODULE_COVER_REFERENCES);
}

export async function saveProductCatalogFilter({
  collectionName,
  docId,
  filterId,
  name,
  order,
  categoryIds,
  cardEntries,
}) {
  const trimmedName = String(name || '').trim();
  if (!FILTER_NAME_REGEX.test(trimmedName)) {
    throw new Error('Digite até 3 palavras sem caracteres especiais.');
  }

  const orderNum =
    order != null && order !== ''
      ? parseInt(String(order), 10)
      : null;

  if (orderNum != null && Number.isNaN(orderNum)) {
    throw new Error('Informe um número válido para a ordem.');
  }

  const slug = nameToFilterSlug(trimmedName);
  if (!slug) {
    throw new Error('Nome do filtro inválido.');
  }

  const colRef = filtersCollectionRef(collectionName, docId);

  if (!filterId) {
    const existing = await getDocs(colRef);
    const duplicate = existing.docs.some((docSnap) => docSnap.data().slug === slug);
    if (duplicate) {
      throw new Error('Já existe um filtro com esse nome neste produto.');
    }

    const newRef = doc(colRef);
    const resolvedOrder = orderNum ?? existing.docs.length + 1;

    await setDoc(newRef, {
      name: trimmedName,
      slug,
      order: resolvedOrder,
      archived: false,
      categoryIds,
      cardEntries,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return newRef.id;
  }

  const filterRef = doc(colRef, filterId);
  const currentSnap = await getDoc(filterRef);
  if (!currentSnap.exists()) {
    throw new Error('Filtro não encontrado.');
  }

  const currentSlug = currentSnap.data().slug;
  if (slug !== currentSlug) {
    const existing = await getDocs(colRef);
    const duplicate = existing.docs.some(
      (docSnap) => docSnap.id !== filterId && docSnap.data().slug === slug
    );
    if (duplicate) {
      throw new Error('Já existe um filtro com esse nome neste produto.');
    }
  }

  await updateDoc(filterRef, {
    name: trimmedName,
    slug,
    ...(orderNum != null ? { order: orderNum } : {}),
    categoryIds,
    cardEntries,
    updatedAt: serverTimestamp(),
  });

  return filterId;
}

export async function setProductCatalogFilterArchived(collectionName, docId, filterId, archived) {
  await updateDoc(doc(firestore, collectionName, docId, CATALOG_FILTERS_SUBCOLLECTION, filterId), {
    archived,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteProductCatalogFilter(collectionName, docId, filterId) {
  await deleteDoc(doc(firestore, collectionName, docId, CATALOG_FILTERS_SUBCOLLECTION, filterId));
}

export async function reorderProductCatalogFilters(collectionName, docId, orderedFilters) {
  const batch = writeBatch(firestore);

  orderedFilters.forEach((filter, index) => {
    batch.update(
      doc(firestore, collectionName, docId, CATALOG_FILTERS_SUBCOLLECTION, filter.id),
      {
        order: index + 1,
        updatedAt: serverTimestamp(),
      }
    );
  });

  await batch.commit();
}
